import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppointmentSeries } from '@shared/types/index.ts';
import { scheduleApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { useHorizonChange } from '../../hooks/use-horizon-change.ts';
import { Alert, Button, ConfirmDialog } from '../ui.tsx';
import { todayInTimezone } from './format.ts';
import { HorizonFields, horizonFromSeries, horizonMaximumDate, horizonValidationKey } from './horizon-fields.tsx';

export function HorizonCard({
  series,
  writable,
  onChanged,
}: {
  series: AppointmentSeries;
  writable: boolean;
  onChanged: (series: AppointmentSeries) => void;
}) {
  const { t } = useTranslation();
  const { company, currentLocation, user } = useSession();
  const canonical = JSON.stringify(horizonFromSeries(series));
  const [draft, setDraft] = useState(() => horizonFromSeries(series));
  const adopted = useRef(canonical);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [saved, setSaved] = useState<{ visitsAdded: number; visitsRemoved: number } | null>(null);
  const dirty = JSON.stringify(draft) !== adopted.current;
  const maxDate = horizonMaximumDate(todayInTimezone(series.timezone), series.startsOn);
  const invalid = horizonValidationKey(draft, series.startsOn, maxDate);
  useEffect(() => {
    if (JSON.stringify(draftRef.current) === adopted.current) setDraft(horizonFromSeries(series));
    adopted.current = canonical;
  }, [canonical]);
  const change = useHorizonChange({
    draft,
    context: JSON.stringify([
      company?.id,
      currentLocation?.id,
      user?.id,
      series.id,
      series.updatedAt,
      canonical,
      company?.recurringHorizonMonths ?? 3,
    ]),
    writable,
    preview: (input) => scheduleApi.previewSeriesHorizon(series.id, input),
    save: (input, previewToken) => scheduleApi.updateSeriesHorizon(series.id, { ...input, previewToken }),
    onSaved: (result, submitted) => {
      adopted.current = JSON.stringify(horizonFromSeries(result.series));
      if (draftRef.current === submitted) setDraft(horizonFromSeries(result.series));
      setSaved({ visitsAdded: result.visitsAdded, visitsRemoved: result.visitsRemoved });
      onChanged(result.series);
    },
  });
  return (
    <section className="card mb-6" aria-labelledby="series-horizon-heading">
      <h2 id="series-horizon-heading" className="mb-4 text-lg font-semibold">
        {t('recurringHorizon.seriesHeading')}
      </h2>
      <HorizonFields
        value={draft}
        companyMonths={company?.recurringHorizonMonths ?? 3}
        startsOn={series.startsOn}
        maxDate={maxDate}
        disabled={!writable}
        fieldErrors={change.fieldErrors}
        onChange={(next) => {
          change.clear();
          setSaved(null);
          setDraft(next);
        }}
      />
      {!change.online ? <Alert tone="info">{t('recurringHorizon.offline')}</Alert> : null}
      {change.error && !change.confirmation ? <Alert tone="error">{change.error}</Alert> : null}
      {saved ? (
        <Alert tone="success">
          {t('recurringHorizon.seriesSaved', { added: saved.visitsAdded, removed: saved.visitsRemoved })}
        </Alert>
      ) : null}
      {writable ? (
        <div className="mt-4">
          {dirty && invalid ? <p className="mb-3 text-sm text-[color:var(--color-danger)]">{t(invalid)}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              busy={change.phase !== 'idle'}
              disabled={!dirty || Boolean(invalid) || !change.online}
              onClick={change.submit}
            >
              {t('recurringHorizon.saveSeries')}
            </Button>
            {change.phase === 'previewing' ? <Button onClick={change.cancel}>{t('common.cancel')}</Button> : null}
            {dirty && change.phase === 'idle' ? (
              <Button
                onClick={() => {
                  change.clear();
                  setDraft(horizonFromSeries(series));
                  setSaved(null);
                }}
              >
                {t('settings.discard')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      {change.confirmation ? (
        <ConfirmDialog
          title={t('recurringHorizon.confirmTitle')}
          confirmLabel={t('recurringHorizon.continue')}
          busy={change.phase === 'saving'}
          confirmDisabled={!change.online}
          error={change.error}
          onConfirm={change.confirm}
          onCancel={change.cancel}
        >
          <p>
            {t(
              change.confirmation.removeCount > 0 ? 'recurringHorizon.confirmRemoval' : 'recurringHorizon.reviewAgain',
              { count: change.confirmation.removeCount },
            )}
          </p>
          {change.confirmation.protectedCount > 0 ? (
            <p className="mt-3">{t('recurringHorizon.protected', { count: change.confirmation.protectedCount })}</p>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </section>
  );
}
